import React, { useEffect, useState, useCallback } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import PilotProfile from '../Profile/PilotProfile';
import ArtRequestOption, {
  ArtRequestOptionValues
} from '../ArtSimRequestOption/ArtSimRequestOption';
// import ArtSimRequestOption from '../ArtRequestOption/ArtRequestOption';
import { useFreezePeriod } from '../../providers/FreezePeriod';
import { Button, Grid } from '@mui/material';
import ArtSimTable from '../ArtSimTable/artSimTable';
import ArtSimFormAccess from './ArtSimFormAccess';
import ArtSimFormRestricted from './ArtSimFormRestricted';
import ArtFormSubmitted from './ArtSimFormSubmitted';
import { useSimArtPilot } from '../../providers/SimArtPilotProvider';
import './ArtRequestForm.scss';
import { useProfile } from '../../providers/Profile';
import {
  SIM_PRIORITY_OPTIONS,
  SIM_CLASS_OPTIONS_MAP,
  SIM_CLASS_OPTIONS,
  SIM_CLASS_REVERSE_MAP,
  CLASS_INFO,
  ART_CLASS_OPTIONS_MAP,
  ART_CLASS_REVERSE_MAP,
  ART_CLASS_INFO,
  ART_CLASS_TABLE_DISPLAY
} from '../../constants/simArt';
import Loader from '../Loader/Loader';
import AcDialog from '../AcDialog/AcDialog';
import { checkSimAccess, checkArtAccess } from '../../helpers/simAccessControl';

interface ArtRequestFormProps {
  title?: string;
  mode?: 'ART' | 'SIM';
}

const ArtRequestForm: React.FC<ArtRequestFormProps> = ({ mode, title }) => {
  dayjs.extend(utc);

  const profileContext = useProfile();
  const profileData = profileContext?.profile?.results;
  const freezePeriodContext = useFreezePeriod();
  const simSubmission = freezePeriodContext?.freezePeriodData?.simSubmission;
  const artSubmission = freezePeriodContext?.freezePeriodData?.artSubmission;
  const [rows, setRows] = useState<any[]>([]);
  const [comment, setComment] = useState('');
  const {
    saveSimRequest,
    simRetrieveRequests,
    submitSimRequest,
    deleteSimRequest,
    retrieveData,
    loading,
    submitData,
    // ART methods/state added
    artRetrieveRequests,
    saveArtRequest,
    deleteArtRequest,
    submitArtRequest,
    artRetrieveData,
    artLoading,
    artSubmitData
  } = useSimArtPilot();
  const [selectedRowForComments, setSelectedRowForComments] = useState<
    string | null
  >(null);
  const [valuesToEdit, setValuesToEdit] =
    useState<ArtRequestOptionValues | null>(null);
  // start with ART base location as null so user always starts with placeholder
  // we will persist it in this state so it remains visible across edits/saves/deletes during the session.
  const [persistentArtLocation, setPersistentArtLocation] = useState<
    string | null
  >(null);
  const [saveCount, setSaveCount] = useState(0);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [showSubmittedModal, setShowSubmittedModal] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const MAX_SAVES = 3;
  const [showTable, setShowTable] = useState(false);
  const [errMsg, setErrMsg] = useState('');
  const [showModal, setShowModal] = useState(false);
  const isLoading =
    (mode === 'ART' ? artLoading : loading) ||
    profileContext?.loading ||
    freezePeriodContext?.loading ||
    !profileContext?.profile?.results;

  const getSimParamsFromSubmission = (subs?: any[]) => {
    const now = new Date();
    if (!Array.isArray(subs) || subs.length === 0) {
      return { simYear: now.getFullYear(), simMonth: now.getMonth() + 1 };
    }
    const withHref = subs.find(
      (s) => !!s?.href && (s.sim_month || s.sim_month === 0)
    );
    const source = withHref ?? subs[0];
    const simMonth = Number(source?.sim_month ?? now.getMonth() + 1);
    const simYear = Number(
      source?.sim_Year ?? source?.sim_year ?? now.getFullYear()
    );
    return { simYear, simMonth };
  };

  useEffect(() => {
    if (mode !== 'ART') return;
    if (persistentArtLocation) return; // don't override if user already picked

    // Make sure artRetrieveData is the array you expect
    if (Array.isArray(artRetrieveData) && artRetrieveData.length > 0) {
      // Depending on your API shape, artBaseLocation might be on the first item
      const codeFromServer =
        artRetrieveData[0]?.artBaseLocation ??
        artRetrieveData.find((it: any) => it?.artBaseLocation)?.artBaseLocation;

      if (codeFromServer) {
        setPersistentArtLocation(codeFromServer); // store CODE (e.g., 'YYZ')
      }
    }
  }, [mode, artRetrieveData, persistentArtLocation]);

  const getArtParamsFromSubmission = (subs?: any[]) => {
    const now = new Date();
    if (!Array.isArray(subs) || subs.length === 0) {
      return { simYear: now.getFullYear(), simMonth: now.getMonth() + 1 };
    }
    const withHref = subs.find(
      (s) => !!s?.href && (s.art_month || s.art_month === 0)
    );
    const source = withHref ?? subs[0];
    const simMonth = Number(source?.art_month ?? now.getMonth() + 1);
    const simYear = Number(
      source?.sim_Year ?? source?.art_year ?? now.getFullYear()
    );
    return { simYear, simMonth };
  };

  const { simYear: currentSimYear, simMonth: currentSimMonth } =
    mode === 'ART'
      ? getArtParamsFromSubmission(artSubmission)
      : getSimParamsFromSubmission(simSubmission);

  // For ART requests, we need to show the target month (next month) in the calendar
  // instead of the submission month
  const getCalendarMonthYear = () => {
    if (mode === 'ART') {
      // ART requests are for the next month after the submission month
      let targetMonth = currentSimMonth;
      let targetYear = currentSimYear;

      // Handle year rollover (December -> January)
      if (targetMonth > 12) {
        targetMonth = 1;
        targetYear = targetYear + 1;
      }

      return { month: targetMonth, year: targetYear };
    }

    // For SIM requests, use the submission month as-is
    return { month: currentSimMonth, year: currentSimYear };
  };

  const { month: calendarMonth, year: calendarYear } = getCalendarMonthYear();

  // Check if pilots have access to the SIM/ART request form
  const accessInfo =
    mode === 'ART'
      ? checkArtAccess(artSubmission)
      : checkSimAccess(simSubmission);
  // Fetch freeze period data on component mount to ensure fresh data
  useEffect(() => {
    if (freezePeriodContext?.getFreezePeriod) {
      freezePeriodContext.getFreezePeriod();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // REMOVED: localStorage persistence for ART location to ensure users always start with placeholder
  // useEffect(() => {
  //   if (mode === 'ART' && typeof window !== 'undefined') {
  //     if (
  //       localStorage.getItem('artPersistentLocation') &&
  //       persistentArtLocation
  //     ) {
  //       localStorage.setItem('artPersistentLocation', persistentArtLocation);
  //     } else {
  //       localStorage.removeItem('artPersistentLocation');
  //     }
  //   }
  // }, [persistentArtLocation, mode]);

  // Clear any existing localStorage data to ensure clean start
  // useEffect(() => {
  //   if (mode === 'ART' && typeof window !== 'undefined') {
  //     localStorage.removeItem('artPersistentLocation');
  //   }
  // }, [mode]);

  // fetch on mount / when sim params change
  useEffect(() => {
    async function fetchRequests() {
      try {
        if (mode === 'ART') {
          await artRetrieveRequests({
            artYear: currentSimYear,
            artMonth: currentSimMonth
          });
        } else {
          await simRetrieveRequests({
            simYear: currentSimYear,
            simMonth: currentSimMonth
          });
        }
      } catch (e) {
        console.warn('fetchRequests failed', e);
      }
    }
    fetchRequests();
  }, [
    simRetrieveRequests,
    currentSimYear,
    currentSimMonth,
    artRetrieveRequests,
    mode
  ]);

  // Helper function to normalize session data from server - mode-aware
  const normalizeSession = useCallback(
    (serverSession: string | any) => {
      if (!serverSession) return '';

      // Handle new backend format as per their requirement: { BEValue: "EarlyMorningSession", FEValue: "Early morning session" }
      if (typeof serverSession === 'object' && serverSession.FEValue) {
        return serverSession.FEValue;
      }

      // Handle if backend sends just the BEValue key
      if (typeof serverSession === 'string') {
        // Use mode-aware class options map
        const displayMap =
          mode === 'ART' ? ART_CLASS_OPTIONS_MAP : SIM_CLASS_OPTIONS_MAP;
        const displayValue = displayMap[serverSession];
        if (displayValue) {
          return displayValue;
        }

        // Fallback to old string matching logic
        const trimmed = serverSession.trim().toLowerCase();

        if (trimmed.includes('any time') || trimmed.includes('anytime')) {
          return 'Any time';
        }
        if (trimmed.includes('early morning')) {
          return 'Early morning session';
        }
        if (trimmed.includes('late morning')) {
          return 'Late morning session';
        }
        if (trimmed.includes('afternoon')) {
          return 'Afternoon session';
        }
        if (trimmed.includes('evening') && !trimmed.includes('late')) {
          return 'Evening session';
        }
        if (trimmed.includes('late evening')) {
          return 'Late Evening session';
        }
      }

      // Fallback - return the original session or first option
      return typeof serverSession === 'string' ? serverSession : 'Any time';
    },
    [mode]
  );

  // Helper function to normalize session data specifically for TABLE display
  // For ART: converts AM -> "Morning session", PM -> "Evening session"
  const normalizeSessionForTable = useCallback(
    (serverSession: string | any) => {
      if (!serverSession) return '';

      // Handle new backend format
      if (typeof serverSession === 'object' && serverSession.FEValue) {
        const feValue = serverSession.FEValue;
        // If ART mode, map AM/PM to full session names for table
        if (mode === 'ART' && ART_CLASS_TABLE_DISPLAY[feValue]) {
          return ART_CLASS_TABLE_DISPLAY[feValue];
        }
        return feValue;
      }

      // Handle if backend sends just the BEValue key
      if (typeof serverSession === 'string') {
        // For ART mode, use table display mapping
        if (mode === 'ART') {
          const tableDisplay = ART_CLASS_TABLE_DISPLAY[serverSession];
          if (tableDisplay) {
            return tableDisplay;
          }
        }

        // For SIM mode, use regular display map
        const displayMap =
          mode === 'ART' ? ART_CLASS_OPTIONS_MAP : SIM_CLASS_OPTIONS_MAP;
        const displayValue = displayMap[serverSession];
        if (displayValue) {
          // If ART and found in display map, check if we need to convert to table format
          if (mode === 'ART' && ART_CLASS_TABLE_DISPLAY[displayValue]) {
            return ART_CLASS_TABLE_DISPLAY[displayValue];
          }
          return displayValue;
        }
      }

      // Fallback
      return typeof serverSession === 'string' ? serverSession : 'Any time';
    },
    [mode]
  );

  // map provider retrieveData into local rows for the table
  useEffect(() => {
    const source = mode === 'ART' ? artRetrieveData : retrieveData;
    if (!Array.isArray(source) || source.length === 0) {
      setRows([]);
      setShowTable(false);
      // Don't clear persistent location when no data exists - user may have selected a location
      // but not saved any requests yet. Only clear on initial load or when switching modes.
      return;
    }

    // Auto-populate ART base location from first item in server response
    // Only set if we don't already have a persistent location (don't override user selection)
    // REMOVED: This auto-population was causing the dropdown to be pre-filled instead of showing placeholder
    // if (
    //   mode === 'ART' &&
    //   source.length > 0 &&
    //   source[0].artBaseLocation &&
    //   !persistentArtLocation
    // ) {
    //   setPersistentArtLocation(source[0].artBaseLocation);
    // }

    const dj = (val: any) => {
      if (!val) return null;
      if (dayjs.isDayjs(val)) return val;
      let s = String(val).trim();
      // date-only strings -> parse as local date to avoid UTC shift
      if (/\dT\d.*Z$/.test(s)) s = s.replace(/Z$/, '');

      // date-only strings -> parse as local date to avoid UTC shift
      if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return dayjs(s, 'YYYY-MM-DD');
      // ISO with time (now without Z)
      if (/\dT\d/.test(s)) return dayjs(s);
      // fallback
      return dayjs(s);
    };

    const serverRows = source.map((item: any, idx: number) => {
      const id = item.simRequestId ?? item.artRequestId;
      const fromDj = dj(item.simFromDt ?? item.artFromDt);
      const toDj = dj(item.simToDt ?? item.artToDt);

      // ISO for table date and human label like "November 14"
      const fromIso = fromDj
        ? fromDj.format('YYYY-MM-DD')
        : item.simFromDt
          ? dayjs(item.simFromDt).format('YYYY-MM-DD')
          : '';
      return {
        id,
        year: item.simYear ?? item.artYear ?? new Date().getFullYear(),
        date: fromIso,
        session: normalizeSessionForTable(
          item.simSession ?? item.artSession ?? ''
        ),
        status: numberToPriority(
          item.simPriorityOrder ?? item.artPriorityOrder,
          item.simRequestType ?? item.artRequestType
        ),
        // type: item.simRequestType === 'avoid' ? 'Avoid' : 'Plan',
        // earlyScheduling: !!item.preferredEarlyScheduling,

        payload: {
          id,
          location: '', // server response doesn't include location here
          classOption: normalizeSession(
            item.simSession ?? item.artSession ?? ''
          ),
          earlyScheduling:
            (item.preferredEarlyScheduling ??
              item.artPreferredEarlyScheduling ??
              item.preferredEarlyScheduling) === 1 ||
            item.preferredEarlyScheduling === 1,
          priority: numberToPriority(
            item.simPriorityOrder ?? item.artPriorityOrder,
            item.simRequestType ?? item.artRequestType
          ),
          range: {
            startDate: fromDj ?? null,
            endDate: toDj ?? null
          },
          comment: '',
          type:
            mode === 'ART'
              ? item.artRequestType === 'avoid'
                ? 'Avoid'
                : 'Plan'
              : item.simRequestType === 'avoid'
                ? 'Avoid'
                : 'Plan'
        },
        _server: item
      };
    });

    // Sort the rows by priority order (1st Request, 2nd Request, 3rd Request)
    const sortedRows = serverRows.sort((a, b) => {
      const priorityA = a.status; // "1st Request", "2nd Request", etc.
      const priorityB = b.status;

      // Extract the number from priority strings for comparison
      const getNumberFromPriority = (priority: string): number => {
        if (!priority) return 999; // Put empty/undefined priorities at the end
        const match = priority.match(/^(\d+)(st|nd|rd|th)\s+Request$/i);
        return match ? parseInt(match[1]) : 999;
      };

      const numA = getNumberFromPriority(priorityA);
      const numB = getNumberFromPriority(priorityB);

      return numA - numB; // Ascending order (1, 2, 3)
    });

    setRows(sortedRows);
    setShowTable(sortedRows.length > 0);
  }, [
    retrieveData,
    artRetrieveData,
    mode,
    normalizeSession,
    normalizeSessionForTable,
    persistentArtLocation
  ]);

  const splitNumberAndText = (s: string) => {
    // if (s === 'N/A') {
    //   return 0;
    // }

    const m = String(s || '').match(/^\s*(\d+)(?:st|nd|rd|th)?\s*(.*)$/i);
    const numberPriority = m ? Number(m[1]) : 0;
    return numberPriority;
  };

  /** number -> label  */
  const numberToPriority = (n?: any | null, type?: string): string => {
    // if (n === '0') return 'N/A';

    if (!n) return '';
    return SIM_PRIORITY_OPTIONS[n - 1];
  };

  // Helper function to extract location code from location string
  const extractLocationCode = useCallback((location: string): string => {
    if (!location) return '';

    // If it's already just a code (e.g., "YUL"), return as is
    if (!location.includes(' - ') && location.length <= 4) {
      return location;
    }

    // Extract code from format like "Montréal - YUL" -> "YUL"
    const match = location.match(/ - ([A-Z]{3,4})$/);
    return match ? match[1] : location;
  }, []);

  async function handleSave(values: ArtRequestOptionValues) {
    const start = values.range?.startDate as any;
    const end = values.range?.endDate as any;
    const simFromDt = start ? start.format('YYYY-MM-DD') : null;
    const simToDt = end
      ? end.format('YYYY-MM-DD')
      : end == null && start.format('YYYY-MM-DD');

    // Create session data in the format requested by backend
    // values.classOption is already the backend key from ArtSimRequestOption
    const sessionBackendKey = values.classOption ?? '';

    // Use mode-aware class options map for display value
    const classOptionsMap =
      mode === 'ART' ? ART_CLASS_OPTIONS_MAP : SIM_CLASS_OPTIONS_MAP;
    const sessionDisplayValue = sessionBackendKey
      ? classOptionsMap[sessionBackendKey] || sessionBackendKey
      : '';

    const simSessionObject = sessionDisplayValue
      ? {
          BEValue: sessionBackendKey, // "AM" or "EarlyMorningSession"
          FEValue: sessionDisplayValue // "AM" or "Early morning session"
        }
      : null;

    let Payload;
    if (values.id) {
      if (mode === 'ART') {
        Payload = {
          artRequestId: values.id,
          artYear: start ? start.year() : new Date().getFullYear(),
          artMonth: start ? start.month() + 1 : new Date().getMonth(),
          artFromDt: simFromDt,
          artToDt: simToDt,
          artPriorityOrder: splitNumberAndText(values.priority ?? '0'),
          artSession: simSessionObject,
          artRequestType: (values.type ?? 'Plan').toLowerCase(),
          pilotPosition: (() => {
            const loc = profileData?.baseLocation?.toString().trim();
            const equip = profileData?.equipment?.toString()?.trim() ?? '';
            const pos = profileData?.position?.toString().trim();
            const parts = [loc, equip, pos].map(
              (p) => p && p.replace(/\|/g, '')
            );
            return parts.join('|');
          })(),
          preferredEarlyScheduling: !!values.earlyScheduling,
          artBaseLocation: extractLocationCode(persistentArtLocation || '')
        };
      } else {
        Payload = {
          simRequestId: values.id,
          simYear: start ? start.year() : new Date().getFullYear(),
          simMonth: start ? start.month() + 1 : new Date().getMonth(),
          simFromDt,
          simToDt,
          simPriorityOrder: splitNumberAndText(values.priority ?? '0'),
          simSession: simSessionObject,
          simRequestType: (values.type ?? 'Plan').toLowerCase(), // 'plan' or 'avoid'
          pilotPosition: (() => {
            const loc = profileData?.baseLocation?.toString().trim();
            const equip = profileData?.equipment?.toString()?.trim() ?? '';
            const pos = profileData?.position?.toString().trim();
            const parts = [loc, equip, pos].map(
              (p) => p && p.replace(/\|/g, '')
            );
            return parts.join('|');
          })(),
          preferredEarlyScheduling: !!values.earlyScheduling
        };
      }
    } else {
      if (mode === 'ART') {
        Payload = {
          artYear: start ? start.year() : new Date().getFullYear(),
          artMonth: start ? start.month() + 1 : new Date().getMonth(),
          artFromDt: simFromDt,
          artToDt: simToDt,
          artPriorityOrder: splitNumberAndText(values.priority ?? '0'),
          artSession: simSessionObject,
          artRequestType: (values.type ?? 'Plan').toLowerCase(),
          pilotPosition: (() => {
            const loc = profileData?.baseLocation?.toString().trim();
            const equip = profileData?.equipment?.toString()?.trim() ?? '';
            const pos = profileData?.position?.toString().trim();
            const parts = [loc, equip, pos].map(
              (p) => p && p.replace(/\|/g, '')
            );
            return parts.join('|');
          })(),
          preferredEarlyScheduling: !!values.earlyScheduling,
          artBaseLocation: extractLocationCode(persistentArtLocation || '')
        };
      } else {
        Payload = {
          simYear: start ? start.year() : new Date().getFullYear(),
          simMonth: start ? start.month() + 1 : new Date().getMonth(),
          simFromDt,
          simToDt,
          simPriorityOrder: splitNumberAndText(values.priority ?? '0'),
          simSession: simSessionObject,
          simRequestType: (values.type ?? 'Plan').toLowerCase(), // 'plan' or 'avoid'
          pilotPosition: (() => {
            const loc = profileData?.baseLocation?.toString().trim();
            const equip = profileData?.equipment?.toString()?.trim() ?? '';
            const pos = profileData?.position?.toString().trim();
            const parts = [loc, equip, pos].map(
              (p) => p && p.replace(/\|/g, '')
            );
            return parts.join('|');
          })(),
          preferredEarlyScheduling: !!values.earlyScheduling
        };
      }
    }
    try {
      if (mode === 'ART') {
        await saveArtRequest(Payload);
      } else {
        await saveSimRequest(Payload);
      }
    } catch (e: any) {
      const errorMessage =
        e?.data?.details ||
        e?.message ||
        'An error occurred while submitting the request';
      setErrMsg(errorMessage);
      setShowModal(true);
      return;
    }

    // If ART mode and the user supplied/changed a base location, persist it so
    // clearing the edit values does not clear the shown location in the form.
    if (mode === 'ART' && values.location) {
      setPersistentArtLocation(extractLocationCode(values.location));
    }

    // add new row and increment save count
    const id = `art-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    setSaveCount((prev) => prev + 1);
    setSelectedRowForComments(id);
    // }
    setShowTable(true);
    setValuesToEdit(null);
  }

  const usedPriorities = Array.from(
    new Set(
      rows
        .map((r) =>
          String(
            // prefer payload.priority (saved form) then row.status (display label)
            r.payload?.priority ?? r.status ?? ''
          ).trim()
        )
        .filter((s) => s.length > 0)
    )
  );

  function handleDelete(row: any) {
    if (row.payload) {
      const artDeleteValues = {
        artRequestId: row.id as number,
        artYear: row.payload.range?.startDate
          ? (row.payload.range.startDate as any).year()
          : new Date().getFullYear(),
        artMonth: row.payload.range?.startDate
          ? (row.payload.range.startDate as any).month() + 1
          : new Date().getMonth() + 1,
        action: 'delete'
      };

      const simDeleteValues = {
        simRequestId: row.id,
        simYear: row.payload.range?.startDate
          ? (row.payload.range.startDate as any).year()
          : new Date().getFullYear(),
        simMonth: row.payload.range?.startDate
          ? (row.payload.range.startDate as any).month() + 1
          : new Date().getMonth() + 1,
        action: 'delete'
      };

      setValuesToEdit(null);
      setSelectedRowForComments(null);
      // optimistically remove the row from UI
      setRows((prev) => prev.filter((r) => r.id !== row.id));
      // decrement save count
      setSaveCount((prev) => (prev > 0 ? prev - 1 : 0));
      // call delete API
      (async () => {
        try {
          if (mode === 'ART') {
            await deleteArtRequest(artDeleteValues);
            setPersistentArtLocation(null);
          } else await deleteSimRequest(simDeleteValues);
        } catch (e: any) {
          const errorMessage =
            e?.data?.details ||
            e?.message ||
            'An error occurred while deleting the request';

          setErrMsg(errorMessage);
          setShowModal(true);
          return;
        }
      })();
    }
    // bring the form into view
    // window.scrollTo({ top: 0, behavior: 'smooth' });
  }
  function handleEdit(row: any) {
    if (row.payload) {
      const editValues = {
        ...row.payload,
        id: row.id,
        priority: row.status
      };
      // If ART mode and there is no explicit location on the payload, fall back
      // to the persistent ART location so the form shows the previously selected base.
      if (
        mode === 'ART' &&
        (!editValues.location || editValues.location === '')
      ) {
        editValues.location = persistentArtLocation ?? '';
      }
      setValuesToEdit(editValues);
    } else {
      setValuesToEdit({
        id: row.id,
        location: '',
        classOption: normalizeSession(row.session),
        priority: row.status,
        range: {
          startDate: row.date
            ? (window as any).dayjs?.(row.date) ?? null
            : null,
          endDate: null
        },
        comment: ''
      });
    }
    // bring the form into view
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function handleSubmit() {
    // if (rows.length === 0) {
    //   return;
    // }

    try {
      if (mode === 'ART') {
        const submissionData = {
          artMonth: String(currentSimMonth),
          artYear: String(currentSimYear),
          artBaseLocation: extractLocationCode(persistentArtLocation || ''),
          specialRequest: comment
        };
        await submitArtRequest(submissionData);
      } else {
        const submissionData = {
          simMonth: String(currentSimMonth),
          simYear: String(currentSimYear),
          specialRequest: comment
        };
        await submitSimRequest(submissionData);
      }
      setHasSubmitted(true);
      setShowSubmittedModal(true);
    } catch (error: any) {
      const errorMessage =
        error?.data?.details ||
        error?.message ||
        'An error occurred while deleting the request';

      setErrMsg(errorMessage);
      setShowModal(true);
      setHasSubmitted(false);
      return;
    }
  }

  function handleCommentUpdate(comment: string) {
    setComment(comment);
  }

  function handleCloseSubmittedModal() {
    setShowSubmittedModal(false);
    setComment('');
    setHasSubmitted(false);
    setRows([]);
    setSaveCount(0);
    setValuesToEdit(null);
    setSelectedRowForComments(null);
    setShowTable(false);
    setFormKey((k) => k + 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    window.location.href = '/';
  }

  if (isLoading) {
    return <Loader />;
  }

  // Check if access is restricted before allowing form interaction
  if (!accessInfo.isAccessible) {
    return (
      <div className="art-request-form">
        <Grid className="header-grey header-content">
          <h1>{title}</h1>
        </Grid>
        <Grid className="header-grey header-content">
          <ArtSimFormRestricted
            mode={mode}
            title={title}
            nextPeriod={accessInfo.nextPeriod}
            reason={accessInfo.reason}
          />
        </Grid>
      </div>
    );
  }

  // Don't render the DataGrid if there's an error being displayed
  if (showModal) {
    return (
      <>
        {showModal && (
          <AcDialog
            open={showModal}
            onClose={() => {
              setShowModal(false);
            }}
            data={{
              icon: 'icon-sad-emoji',
              title: 'Error',
              description: errMsg,
              actionLabel: 'Close',
              handleAction: () => {
                setShowModal(false);
              }
            }}
          />
        )}
      </>
    );
  }
  if (
    (submitData && submitData.length) ||
    (artSubmitData && artSubmitData.length)
  ) {
    return (
      <div className="art-request-form">
        <Grid className="header-grey header-content">
          <h1>{title}</h1>
          <PilotProfile />
        </Grid>
        <Grid className="header-grey header-content">
          <ArtSimFormAccess />
        </Grid>
      </div>
    );
  }
  return (
    <div className="art-request-form">
      <Grid className="header-grey header-content">
        <h1>{title}</h1>
        <PilotProfile />
      </Grid>

      <Grid className="header-grey header-content">
        <ArtRequestOption
          key={`art-req-${formKey}`}
          initialValues={
            mode === 'ART' ? { location: persistentArtLocation ?? '' } : {}
          }
          valuesToEdit={valuesToEdit}
          onSave={handleSave}
          onLocationChange={
            mode === 'ART' ? setPersistentArtLocation : undefined
          }
          saveCount={
            mode === 'ART' ? artRetrieveData.length : retrieveData.length
          }
          maxSaves={MAX_SAVES}
          title={title ?? ''}
          mode={mode}
          SimMonth={calendarMonth}
          SimYear={calendarYear}
          usedPriorities={usedPriorities}
          hasSavedRequests={mode === 'ART' && rows.length > 0}
          options={
            mode === 'ART'
              ? Object.values(ART_CLASS_OPTIONS_MAP)
              : SIM_CLASS_OPTIONS
          }
          reverseMap={
            mode === 'ART' ? ART_CLASS_REVERSE_MAP : SIM_CLASS_REVERSE_MAP
          }
          classInfo={mode === 'ART' ? ART_CLASS_INFO : CLASS_INFO}
          priorityOptions={SIM_PRIORITY_OPTIONS}
          // disabled={loading || saveCount >= MAX_SAVES || hasSubmitted}
        />
      </Grid>
      {showTable && rows.length > 0 && (
        <>
          <Grid className="header-grey header-content">
            <ArtSimTable
              rows={rows}
              loading={mode === 'ART' ? artLoading : loading}
              onEdit={handleEdit}
              onDelete={handleDelete}
              comment={comment}
              onCommentUpdate={handleCommentUpdate}
              selectedRowForComments={selectedRowForComments}
              onRowSelect={setSelectedRowForComments}
            />
          </Grid>

          <Grid container justifyContent="center" spacing={4} sx={{ mt: 4 }}>
            <Grid item>
              <Button
                onClick={() => {
                  // Clear form editing state
                  setValuesToEdit(null);
                  setSelectedRowForComments(null);
                  // Reset comment field
                  setComment('');
                  // Clear any error messages
                  setErrMsg('');
                  setShowModal(false);
                  // Don't remount the component - just let the useEffect handle the reset
                }}
                disabled={hasSubmitted || (!rows.length && saveCount >= 3)}
                className="art-request-cancel"
              >
                Cancel
              </Button>
            </Grid>

            <Grid item>
              <Button
                className="art-request-submit"
                onClick={handleSubmit}
                disabled={
                  hasSubmitted ||
                  (!valuesToEdit?.classOption &&
                    !valuesToEdit?.priority &&
                    !valuesToEdit?.range &&
                    saveCount >= 3)
                }
                // variant="contained"
              >
                {hasSubmitted ? 'Submitted' : 'Submit'}
              </Button>
            </Grid>
          </Grid>
        </>
      )}
      {/* Submission Success  */}
      <ArtFormSubmitted
        open={showSubmittedModal}
        onClose={handleCloseSubmittedModal}
      />
    </div>
  );
};

export default ArtRequestForm;
